import * as FileSystem from '@/lib/fileSystemLegacy';
import type { Timestamp } from 'firebase/firestore';

export type AttachmentSource = 'email' | 'upload' | 'manual' | 'sync';

export type ItineraryAttachment = {
  id: string;
  name: string;
  url: string;
  contentType?: string | null;
  size?: number | null;
  source?: AttachmentSource;
  createdAt?: Date;
  offlinePath?: string | null;
  expiresAt?: Date | null;
  confidence?: number;
};

export type LegacyAttachmentInput = {
  url: string;
  name?: string;
  contentType?: string | null;
  size?: number | null;
  source?: AttachmentSource;
};

export const ATTACHMENT_SIZE_LIMIT_BYTES = 10 * 1024 * 1024; // 10MB
const OFFLINE_DIR = `${FileSystem.documentDirectory}itinerary_attachments/`;
const PREFETCH_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h before start date
const id = () => Math.random().toString(36).slice(2, 10);

/** Stable short id from URL for cache filenames and legacy rows (FNV-1a). */
function stableIdFromUrl(url: string): string {
  const s = (url || '').trim();
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function inferAttachmentExtension(att: ItineraryAttachment): string {
  const fromName = att.name?.split('.').pop()?.replace(/[^a-zA-Z0-9]/g, '');
  if (fromName && fromName.length >= 2 && fromName.length <= 8) {
    return fromName.toLowerCase();
  }
  const tail = att.url?.split('/').pop()?.split('?')[0];
  if (tail?.includes('.')) {
    const e = tail.split('.').pop()?.replace(/[^a-zA-Z0-9]/g, '');
    if (e && e.length >= 2 && e.length <= 8) return e.toLowerCase();
  }
  return 'bin';
}

/** Predictable on-disk path for an itinerary attachment (matches download cache). */
export function localItineraryAttachmentCachePath(att: ItineraryAttachment): string {
  const urlStr = (att.url || '').trim();
  const baseId = att.id || (urlStr ? stableIdFromUrl(urlStr) : id());
  return `${OFFLINE_DIR}${baseId}.${inferAttachmentExtension(att)}`;
}

/**
 * If a prefetched/offline copy exists on disk, return a file URI; otherwise null.
 */
export async function resolveItineraryAttachmentLocalUri(att: ItineraryAttachment): Promise<string | null> {
  const tryProbe = async (raw: string): Promise<string | null> => {
    const s = raw.trim();
    if (!s) return null;
    const variants = s.startsWith('file://') ? [s] : [s, `file://${s}`];
    for (const probe of variants) {
      try {
        const info = await FileSystem.getInfoAsync(probe);
        if (info.exists) {
          return probe.startsWith('file://') ? probe : `file://${probe}`;
        }
      } catch {
        /* try next */
      }
    }
    return null;
  };

  if (att.offlinePath) {
    const hit = await tryProbe(att.offlinePath);
    if (hit) return hit;
  }
  return tryProbe(localItineraryAttachmentCachePath(att));
}

/**
 * Normalize attachments coming from Firestore or legacy string arrays.
 */
export function normalizeAttachments(
  attachments?: Array<Partial<ItineraryAttachment> | LegacyAttachmentInput>,
  legacyDocuments: string[] = []
): ItineraryAttachment[] {
  const normalizedFromAttachments = (attachments || []).map((raw, index) =>
    normalizeAttachment(raw, index)
  );

  // Deduplicate attachments by URL (Firestore merge + legacy `documents` can create duplicates).
  const byUrl = new Map<string, ItineraryAttachment>();
  const withoutUrl: ItineraryAttachment[] = [];
  for (const att of normalizedFromAttachments) {
    if (!att) continue;
    const url = (att.url || '').trim();
    if (!url) {
      withoutUrl.push(att);
      continue;
    }
    if (!byUrl.has(url)) byUrl.set(url, att);
  }

  const legacyUnique = (legacyDocuments || [])
    .map((u) => (u || '').trim())
    .filter(Boolean)
    .filter((url) => !byUrl.has(url));

  const normalizedFromLegacy = legacyUnique.map((url, index) =>
    buildLegacyAttachment(url, index)
  );

  return [...byUrl.values(), ...withoutUrl, ...normalizedFromLegacy].filter(Boolean) as ItineraryAttachment[];
}

/**
 * Build a deterministic attachment from a legacy URL-only document.
 */
export function buildLegacyAttachment(url: string, index: number): ItineraryAttachment {
  const rawLastSegment = url.split('/').pop()?.split('?')[0] || `document-${index + 1}`;
  // Firebase download URLs often include an encoded path like `trip-assets%2F...%2Ffile.pdf`
  // Decode and keep only the actual file name for display.
  const decoded = (() => {
    try {
      return decodeURIComponent(rawLastSegment);
    } catch {
      return rawLastSegment;
    }
  })();
  const fileName = decoded.split('/').pop() || decoded || `document-${index + 1}`;
  const extension = fileName.split('.').pop()?.toLowerCase();
  const contentType = extension ? `application/${extension}` : undefined;
  return {
    id: `u-${stableIdFromUrl(url)}`,
    name: fileName,
    url,
    contentType,
    size: null,
    source: 'upload',
    createdAt: undefined,
  };
}

function normalizeAttachment(
  attachment: Partial<ItineraryAttachment> | LegacyAttachmentInput,
  index: number
): ItineraryAttachment {
  const asAny = attachment as any;
  const createdAt = convertDate(asAny.createdAt);
  const urlStr = typeof asAny.url === 'string' ? asAny.url.trim() : '';
  return {
    id: asAny.id || (urlStr ? `u-${stableIdFromUrl(urlStr)}` : `att-${index}-${id()}`),
    name: asAny.name || asAny.filename || 'Attachment',
    url: asAny.url,
    contentType: asAny.contentType ?? asAny.mimeType ?? null,
    size: asAny.size ?? null,
    source: (asAny.source as AttachmentSource) || 'upload',
    createdAt,
    offlinePath: asAny.offlinePath || null,
    expiresAt: convertDate(asAny.expiresAt) || null,
    confidence: typeof asAny.confidence === 'number' ? asAny.confidence : undefined,
  };
}

function convertDate(date?: Date | string | Timestamp | null): Date | undefined {
  if (!date) return undefined;
  if (date instanceof Date) return date;
  if (typeof date === 'string') {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  if (typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
    try {
      return (date as any).toDate();
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Determine if an attachment should be prefetched for offline.
 */
export function shouldPrefetchAttachment(itemStart: Date, now: Date = new Date()): boolean {
  const startTime = itemStart.getTime();
  return startTime - PREFETCH_WINDOW_MS <= now.getTime() && startTime >= now.getTime() - PREFETCH_WINDOW_MS;
}

/**
 * Download a single attachment for offline use.
 */
export async function downloadAttachmentForOffline(attachment: ItineraryAttachment): Promise<ItineraryAttachment> {
  if (!attachment.url) return attachment;
  await ensureOfflineDir();
  const localPath = localItineraryAttachmentCachePath(attachment);
  const info = await FileSystem.getInfoAsync(localPath);
  if (info.exists) {
    return {
      ...attachment,
      offlinePath: localPath,
    };
  }
  const result = await FileSystem.downloadAsync(attachment.url, localPath);
  return {
    ...attachment,
    offlinePath: result.uri,
  };
}

/**
 * Remove a cached attachment file.
 */
export async function removeCachedAttachment(attachment: ItineraryAttachment): Promise<void> {
  if (!attachment.offlinePath) return;
  try {
    const info = await FileSystem.getInfoAsync(attachment.offlinePath);
    if (info.exists) {
      await FileSystem.deleteAsync(attachment.offlinePath, { idempotent: true });
    }
  } catch (error) {
    console.warn('[ItineraryAttachments] Failed to remove cached attachment', error);
  }
}

/**
 * Prefetch attachments for itinerary items happening soon.
 */
export async function prefetchUpcomingAttachments(
  items: Array<{ startDate: Date; attachments?: ItineraryAttachment[] }>
): Promise<{ downloaded: number; failed: number }> {
  const now = new Date();
  let downloaded = 0;
  let failed = 0;

  for (const item of items) {
    if (!shouldPrefetchAttachment(item.startDate, now)) continue;
    for (const attachment of item.attachments || []) {
      if (!attachment.url) continue;
      try {
        const updated = await downloadAttachmentForOffline(attachment);
        attachment.offlinePath = updated.offlinePath;
        downloaded++;
      } catch (error) {
        console.warn('[ItineraryAttachments] Prefetch failed', error);
        failed++;
      }
    }
  }

  return { downloaded, failed };
}

async function ensureOfflineDir(): Promise<void> {
  const info = await FileSystem.getInfoAsync(OFFLINE_DIR);
  if (!info.exists) {
    await FileSystem.makeDirectoryAsync(OFFLINE_DIR, { intermediates: true });
  }
}

/** Delete on-disk itinerary attachment prefetch cache (e.g. on sign-out). */
export async function clearItineraryAttachmentCacheDir(): Promise<void> {
  try {
    const info = await FileSystem.getInfoAsync(OFFLINE_DIR);
    if (info.exists) {
      await FileSystem.deleteAsync(OFFLINE_DIR, { idempotent: true });
    }
  } catch {
    /* best-effort */
  }
}


